import axios from "axios";
import Cookies from "js-cookie";
import { compressImage } from "../utils/compressImage";

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  total?: number;
  totalOrders?: number;
  activeUsers?: number;
  totalRevenue?: number;
  averageOrderValue?: number;
  currentMonthChoiceCounts?: {
    diningIn?: number;
    takeaway?: number;
    online?: number;
  };
  monthlyRevenueLast6Months?: Array<{
    month?: string;
    revenue?: number;
  }>;
  revenueData?: {
    today?: Array<{ label: string; revenue: number }>;
    thisWeek?: Array<{ label: string; revenue: number }>;
    thisMonth?: Array<{ label: string; revenue: number }>;
  };
  trendingItem?: {
    id?: string;
    name?: string;
    imageUrl?: string;
  };
  trendingItems?: {
    daily?: {
      id?: string;
      name?: string;
      imageUrl?: string;
    } | null;
    weekly?: {
      id?: string;
      name?: string;
      imageUrl?: string;
    } | null;
    monthly?: {
      id?: string;
      name?: string;
      imageUrl?: string;
    } | null;
  };
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5001/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Shrink large images before upload (the server rejects bodies over ~1MB).
api.interceptors.request.use(async (config) => {
  const isImageUpload = ["/upload-image", "/menu-import/extract"].some((p) => (config.url || "").includes(p));
  if (isImageUpload && config.data instanceof FormData) {
    const image = config.data.get("image");
    if (image instanceof File) {
      const smaller = await compressImage(image);
      if (smaller !== image) config.data.set("image", smaller, smaller.name);
    }
  }
  return config;
});

// Request interceptor for adding auth token for admin routes
api.interceptors.request.use(
  (config) => {
    // Add auth token for admin routes and protected routes (menu, orders, loyalty)
    const url = config.url || "";
    const path = window.location.pathname || "";
    const isAdminRoute =
      url.includes("/admin") ||
      url.includes("/menu") ||
      url.includes("/orders") ||
      url.includes("/loyalty") ||
      url.includes("/discounts") ||
      url.includes("/newreg") ||
      url.includes("/codes") ||
      url.includes("/categories") ||
      url.includes("/tax") ||
      url.includes("/manage") ||
      url.includes("/roles") ||
      url.includes("/new") ||
      url.includes("/loyalty") ||
      url.includes("/upload-image") ||
      url.includes("/discount") ||
      url.includes("/platform") ||
      url.includes("/promotional-emails") ||
      url.includes("/restaurant-menu") ||
      url.includes("/reports") ||
      url.includes("/transactions") ||
      url.includes("/plans") ||
      url.includes("/ledger") ||
      url.includes("/billing") ||
      url.includes("/presets") ||
      url.includes("/stripe");

    const isKitchenApp = path.startsWith("/kitchen");
    const isSalesApp = path.startsWith("/Sales");

    if (isKitchenApp || isSalesApp) {
      const token = Cookies.get("emptoken");
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    if (isAdminRoute) {
      const token = Cookies.get("token");

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // Remove Authorization header for non-admin routes
      if (config.headers && "Authorization" in config.headers) {
        delete config.headers.Authorization;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for handling auth errors for admin routes
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 for admin and protected routes, but NOT for login route
    const url = error.config?.url || "";
    const isAdminRoute =
      url.includes("/admin") ||
      url.includes("/menu") ||
      url.includes("/orders") ||
      url.includes("/loyalty") ||
      url.includes("/wallet") ||
      url.includes("/reports");
    const isLoginRoute = url.includes("/admin/login");

    const redirectToLogin = () => {
      const path = window.location.pathname || "";
      if (path.startsWith("/kitchen")) return "/kitchen/login";
      if (path.startsWith("/Sales")) return "/login";
      if (
        path.startsWith("/pos") ||
        path.startsWith("/choice") ||
        path.startsWith("/pay")
      ) {
        return "/pos/login";
      }
      return "/admin/login";
    };

    // Only redirect on 401 for admin routes, but NOT for login route
    if (error.response?.status === 401 && isAdminRoute && !isLoginRoute) {
      Cookies.remove("token");
      window.location.href = redirectToLogin();
    }
    return Promise.reject(error);
  },
);

export type { ApiResponse };
export default api;

// Category CRUD API
export const getCategories = (restaurantId?: string) =>
  api.get("/categories", restaurantId ? { params: { restaurantId } } : undefined);
export const getPresets = async (categoryId: string) =>
  api.get(`/presets/category/${categoryId}`);
// Pass the restaurant from screens that may not send a restaurant token (POS).
export const getCodes = (restaurantId?: string) =>
  api.get("/codes", { params: restaurantId ? { restaurantId } : undefined });
export const createCodes = (data: any) => api.post("/codes/create", data);
export const updateCodes = (id: string, data: any) =>
  api.put(`/codes/${id}`, data);
export const deleteCodes = (id: string) => api.delete(`/codes/${id}`);
export const createCategory = (data: { name: string }) =>
  api.post("/categories", data);
export const updateCategory = (id: string, data: { name: string }) =>
  api.put(`/categories/${id}`, data);
export const deleteCategory = (id: string) => api.delete(`/categories/${id}`);
export const checkoutSession = (data: {
  amount: number;
  productName: string;
  hotelId?: string;
}) => {
  return api.post("/stripe/create-checkout-session", data);
};

export const paymentSession = (session_id: string, hotelId?: string) => {
  return api.get(`/stripe/get-payment-session`, { params: { session_id, hotelId } });
};

export const saveTransactions = (data: {
  phoneNumber: string;
  amount: string;
  currency: string;
  transaction_id: string;
}) => {
  return api.post("/stripe/save-transactions", data);
};

// Square Payment API
export const createSquarePayment = (data: {
  token: string;
  amount: number;
  orderId?: string;
  hotelId?: string;
}) => {
  return api.post("/square/payment", data);
};

// Online payment gateways for a restaurant (Razorpay in India, else Square/Stripe)
export type PaymentOptions = {
  country: string;
  currency: string;
  gateways: {
    razorpay?: { keyId: string };
    square?: { applicationId: string; locationId: string; countryCode: string };
    stripe?: { publishableKey: string };
    qr?: { imageUrl: string; upiId: string; payeeName: string };
  };
};
// UPI QR for an order, generated from the restaurant's UPI ID with the amount.
export const getUpiQr = (hotelId: string, amount: number) =>
  api.get<{ success: boolean; data: { dataUrl: string; uri: string } }>("/payments/upi-qr", {
    params: { hotelId, amount },
  });
export const getPaymentOptions = (hotelId: string) =>
  api.get<{ success: boolean; data: PaymentOptions }>("/payments/options", {
    params: { hotelId },
  });
export const createRazorpayOrder = (hotelId: string, amount: number) =>
  api.post<{
    success: boolean;
    data: { orderId: string; amount: number; currency: string; keyId: string };
  }>("/payments/razorpay/order", { hotelId, amount });
export const verifyRazorpayPayment = (data: {
  hotelId: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}) => api.post("/payments/razorpay/verify", data);

export const getTax = (country: string) => api.get(`/tax/${country}`);
export const getTaxGrouped = (country: string = "US") => api.get(`/tax/${country}/grouped`);
export const createTax = (country: string, data: any) => api.post(`/tax/${country}`, data);
export const updateTax = (country: string, id: string, data: any) => api.put(`/tax/${country}/${id}`, data);
export const deleteTax = (country: string, id: string) => api.delete(`/tax/${country}/${id}`);
// Pass the restaurant id to get that restaurant's own logo (falls back to
// the platform logo when it hasn't uploaded one).
export const getPlatformLogo = (hotelId?: string) =>
  api.get(`/platform/logo`, { params: hotelId ? { hotelId } : undefined });

//Roles
export const getRoles = () => api.get("/roles");
export const createRole = (data: { name: string }) => api.post("/roles", data);
export const deleteRole = (id: string) => api.delete(`/roles/${id}`);

//OrderStatus
export const updateOrderStatus = async (id: string, orderStatus: string) =>
  api.patch(`/orders/${id}/status`, { orderStatus });

export const updateOrderDelivery = async (
  id: string,
  deliveryProvider: "DoorDash" | "Uber" | "Custom Delivery",
) => api.patch(`/orders/${id}/delivery`, { deliveryProvider });

export const getDeliveryQuote = async (data: {
  dropoff_address: string;
  dropoff_latitude: number;
  dropoff_longitude: number;
  pickup_time: string;
}) => api.post("/orders/delivery/quote", data);

export const updateOrderDeliveryExternal = async (data: {
  orderId: string;
  platform: string;
  deliveryId?: string;
  tipAmount?: number;
  metadata?: Record<string, any>;
  items?: any[];
  totalAmount?: number;
  userId?: string;
  orderBy?: string;
}) => api.post("/order/update-delivery", data);

export const cancelOrderItem = (orderId: string, itemId: string) => {
  return api.put(`/orders/${orderId}/item/${itemId}/cancel`);
};

export const updateOrderScheduledAt = (orderId: string, scheduledAt: string) =>
  api.patch(`/orders/${orderId}/scheduled-at`, { scheduledAt });

// Preset APIs (using existing getPresets for category-based fetching)
export const getAllPresetsAPI = () => api.get("/presets");
export const createPresetAPI = (data: any) => api.post("/presets", data);
export const updatePresetAPI = (id: string, data: any) =>
  api.put(`/presets/${id}`, data);
export const deletePresetAPI = (id: string) => api.delete(`/presets/${id}`);

// Hotel CRUD API
export const getHotels = () => api.get("/admin/getHotelInformation");
export const updateHotel = (id: string, data: any) =>
  api.put(`/admin/hotels/${id}`, data);
export const deleteHotel = (id: string) => api.delete(`/admin/hotels/${id}`);

// Platform API
export const getPlatform = () => api.get("/platform");

export const updatePlatformLogo = (logo: string) =>
  api.put("/platform/logo", { logo });

export const updateApplePassLogo = (logo: string) =>
  api.put("/platform/apple-pass-logo", { logo });

export const deletePlatformLogo = () => api.delete("/platform/logo");
export const deleteApplePassLogo = () =>
  api.delete("/platform/apple-pass-logo");

export const updatePlatformCurrency = (currency: string) =>
  api.put("/platform/currency", { currency });

// Master admin: image above "Welcome" on the home page ("" removes it).
export const updateWelcomeImage = (welcomeImage: string) =>
  api.put("/platform/welcome-image", { welcomeImage });

// Master admin: the one country the platform operates in (drives tax setup).
export const updatePlatformCountry = (countryCode: string, countryName: string) =>
  api.put("/platform/country", { countryCode, countryName });

export const updatePlatformFontFamily = (fontFamily: string) =>
  api.put("/platform/font-family", { fontFamily });

export const updatePlatformPlaceholderImage = (placeholderImage: string) =>
  api.put("/platform/placeholder-image", { placeholderImage });

export const updatePlatformPrimaryColor = (primaryColor: string) =>
  api.put("/platform/primary-color", { primaryColor });

export const updatePlatformMenuColors = (colors: {
  categoryNameColor: string;
  itemNameColor: string;
  priceColor?: string;
  descriptionColor: string;
  categoryTabColor: string;
  categoryTabText: string;
  categoryTabUnselectedBg: string;
  categorySelectedText: string;
}) => api.put("/platform/menu-colors", colors);

export const getPlatformIntegrations = () => api.get("/platform/integrations");
export const updatePlatformIntegration = (
  provider:
    | "baseEnv"
    | "mail"
    | "applePasskit"
    | "digitalOceanSpaces"
    | "square"
    | "stripe"
    | "razorpay"
    | "billingStripe"
    | "billingRazorpay",
  data: { accountEmail?: string; credentials: Record<string, string> },
) => api.put(`/platform/integrations/${provider}`, data);

// Per-hotel (location) integrations for Uber Direct, DoorDash, Google Places
export const getLocationIntegrations = (hotelId: string) =>
  api.get(`/admin/hotels/${hotelId}/integrations`);
export const updateLocationIntegration = (
  hotelId: string,
  provider:
    | "uberDirect"
    | "doordashDeveloper"
    | "googlePlaces"
    | "square"
    | "stripe"
    | "mail"
    | "razorpay"
    | "qrCode",
  data: { accountEmail?: string; credentials: Record<string, string> },
) => api.put(`/admin/hotels/${hotelId}/integrations/${provider}`, data);

// Places API
export const getPlaceAutocomplete = (
  input: string,
  lat?: number,
  lng?: number,
  sessionToken?: string,
) =>
  api.get(
    `/admin/place-autocomplete?input=${encodeURIComponent(input)}${
      typeof lat === "number" && typeof lng === "number"
        ? `&lat=${lat}&lng=${lng}`
        : ""
    }${sessionToken ? `&sessionToken=${encodeURIComponent(sessionToken)}` : ""}`,
  );
export const getPlaceDetails = (lat: number, lng: number) =>
  api.get(`/admin/place-details?lat=${lat}&lng=${lng}`);
export const getPlaceDetailsById = (placeId: string, sessionToken?: string) =>
  api.get(
    `/admin/place-details-by-id?placeId=${encodeURIComponent(placeId)}${
      sessionToken ? `&sessionToken=${encodeURIComponent(sessionToken)}` : ""
    }`,
  );
export const getPlaceOpeningHours = (lat: number, lng: number) =>
  api.get(`/admin/place-opening-hours?lat=${lat}&lng=${lng}`);

// Restaurant Menu API
export const getRestaurantMenuItems = (hotelId: string) =>
  api.get(`/restaurant-menu/${hotelId}`);
export const updateRestaurantMenuItem = (
  hotelId: string,
  menuId: string,
  data: { price?: number | null; availability?: boolean; fulfillmentMethods?: ("pickup" | "delivery" | "self-ordering")[] | null },
) => api.put(`/restaurant-menu/${hotelId}/${menuId}`, data);
export const bulkUpdateRestaurantMenuItems = (
  hotelId: string,
  items: Array<{
    menuId: string;
    price?: number | null;
    availability?: boolean;
    fulfillmentMethods?: ("pickup" | "delivery" | "self-ordering")[] | null;
  }>,
) => api.post(`/restaurant-menu/${hotelId}/bulk`, { items });
export const deleteRestaurantMenuItem = (hotelId: string, menuId: string) =>
  api.delete(`/restaurant-menu/${hotelId}/${menuId}`);
export const getRestaurantMenuByMenuId = (menuId: string) =>
  api.get(`/restaurant-menu/by-menu/${menuId}`);

// Reward Campaigns API (wallet endpoints)
export const getRewardCampaigns = () => api.get("/admin/getUserGiftCards");
export const createRewardCampaign = (data: {
  amount: number;
  expiresAt: string;
}) => api.post("/admin/createUserGiftCard", data);
export const updateRewardCampaign = (
  id: string,
  data: { amount: number; expiresAt: string },
) => api.put(`/admin/updateUserGiftCard/${id}`, data);
export const deleteRewardCampaign = (id: string) =>
  api.delete(`/admin/deleteUserGiftCard/${id}`);

// Reports API
export const getSalesReport = (params: {
  startDate?: string;
  endDate?: string;
  orderSource?: string;
  orderType?: string;
  restaurantId?: string;
}) => api.get("/reports/sales", { params });

export const getPaymentsReport = (params: {
  startDate?: string;
  endDate?: string;
  orderSource?: string;
  orderType?: string;
  restaurantId?: string;
}) => api.get("/reports/payments", { params });

export const getItemsReport = (params: {
  startDate?: string;
  endDate?: string;
  orderSource?: string;
  orderType?: string;
  restaurantId?: string;
}) => api.get("/reports/items", { params });

export const cancelOrder = (orderId: string) => api.post(`/orders/${orderId}/cancel`);

export const processOrderRefunds = (orderId: string) =>
  api.post(`/transactions/order/${orderId}/process-refunds`);

export const verifyEmployeePasscode = (restaurantId: string, passcode: string) =>
  api.post("/new/verify-passcode", { restaurantId, passcode });

// Location Admin Management API
export const getLocationAdmins = () => api.get("/admin/location-admins");
export const createLocationAdmin = (data: {
  email: string;
  password: string;
  restaurantId: string;
}) => api.post("/admin/location-admins", data);
export const updateLocationAdmin = (
  id: string,
  data: { email?: string; password?: string; restaurantId?: string },
) => api.put(`/admin/location-admins/${id}`, data);
export const deleteLocationAdmin = (id: string) =>
  api.delete(`/admin/location-admins/${id}`);
export const updateLocationAdminStatus = (
  id: string,
  status: "active" | "suspended",
) => api.patch(`/admin/location-admins/${id}/status`, { status });
export const updateLocationAdminApproval = (
  id: string,
  approvalStatus: "pending" | "approved",
) => api.patch(`/admin/location-admins/${id}/approval`, { approvalStatus });
export const switchLocation = (restaurantId: string) =>
  api.post("/admin/switch-location", { restaurantId });

// Plan billing (restaurant admins pay the master admin)
export type BillingConfig = {
  gateway: "stripe" | "razorpay";
  currency: string;
  configured: boolean;
  razorpayKeyId?: string;
};
export const getBillingConfig = () =>
  api.get<{ success: boolean; data: BillingConfig }>("/billing/config");
export const setBillingGateway = (gateway: "stripe" | "razorpay") =>
  api.put("/billing/gateway", { gateway });
export const createPlanRazorpayOrder = (planId: string) =>
  api.post<{
    success: boolean;
    data: { orderId: string; amount: number; currency: string; keyId: string; planName: string };
  }>("/billing/razorpay/order", { planId });
export const verifyPlanRazorpayPayment = (data: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}) => api.post("/billing/razorpay/verify", data);
export const getPlanTransactions = (params: { page?: number; limit?: number; search?: string }) =>
  api.get("/billing/transactions", { params });

// Trial / subscription account info + plans
export const getAdminMe = () => api.get("/admin/me");
export const changeOwnPassword = (data: {
  currentPassword: string;
  newPassword: string;
}) => api.put("/admin/change-password", data);
export const getPlans = () => api.get("/plans");
export const getAllPlans = () => api.get("/plans/all");
export const createPlan = (data: {
  name: string;
  durationMonths: number;
  price: number;
  maxLocations?: number;
}) => api.post("/plans", data);
export const updatePlan = (
  id: string,
  data: {
    name?: string;
    durationMonths?: number;
    price?: number;
    isActive?: boolean;
    maxLocations?: number;
  },
) => api.put(`/plans/${id}`, data);
export const deletePlan = (id: string) => api.delete(`/plans/${id}`);
export const createSubscriptionCheckout = (planId: string) =>
  api.post("/stripe/create-subscription-checkout", { planId });
